import { createFileRoute } from "@tanstack/react-router";
import { loadAcademyLesson } from "~/utils/content-fns";
import { seo, seoLinks } from "~/utils/seo";
import { TableOfContents } from "~/components/TableOfContents";

export const Route = createFileRoute("/c1-academy/$slug")({
  loader: ({ params }) => loadAcademyLesson({ data: { slug: params.slug } }),
  head: ({ loaderData }) => ({
    meta: loaderData
      ? seo({
          title: loaderData.title,
          description: loaderData.description,
          image: loaderData.featured_image,
          url: `/c1-academy/${loaderData.slug}`,
        })
      : [],
    links: loaderData ? seoLinks({ url: `/c1-academy/${loaderData.slug}` }) : [],
  }),
  component: AcademyLesson,
});

/** Convert a URL path like "/guides/scim-provisioning-explained" to "SCIM Provisioning Explained" */
function labelFromUrl(url: string): string {
  const slug = url
    .replace(/\.(md|mdx)$/, "")
    .replace(/\/+$/, "")
    .split("/")
    .pop() || url;
  return slug
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function LearnMoreLinks({ learn_more }: { learn_more: { heading: string; entries: string[] } }) {
  return (
    <div>
      <div className="hidden font-body text-base font-bold text-charcoal md:ml-2 md:block">
        {learn_more.heading}
      </div>
      <ul className="mt-1 hidden font-body text-sm leading-snug md:block">
        {learn_more.entries.map((url) => {
          // Clean up paths: strip .md extension and /content/ prefix
          const href = url
            .replace(/\.(md|mdx)$/, "")
            .replace(/^\/content\//, "/");
          return (
            <li
              key={url}
              className="my-0 rounded-md px-2 py-1 text-gray-60 hover:bg-teal-dark/5 hover:text-charcoal"
            >
              <a href={href}>{labelFromUrl(url)}</a>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function AcademyLesson() {
  const lesson = Route.useLoaderData();
  const hasToc = lesson.toc.length > 0;
  const hasLearnMore = lesson.learn_more && lesson.learn_more.entries.length > 0;
  const hasSidebar = hasToc || hasLearnMore;

  const mainContent = (
    <>
      <a
        href="/c1-academy"
        className="mb-6 inline-flex items-center gap-2 text-sm text-gray-60 hover:text-teal-dark"
      >
        <span>&larr;</span> Back to C1 Academy
      </a>
      <h1 className="mb-4 font-display text-3xl font-bold text-charcoal md:text-4xl">
        {lesson.title}
      </h1>

      <div className="mb-6 flex items-center gap-3 text-sm text-gray-60">
        {lesson.speaker && <span>{lesson.speaker}</span>}
        {lesson.speaker_title && (
          <>
            <span>&middot;</span>
            <span>{lesson.speaker_title}</span>
          </>
        )}
        {lesson.length && (
          <>
            <span>&middot;</span>
            <span>{lesson.length} min</span>
          </>
        )}
      </div>

      {/* Lesson video (Vimeo or legacy Streamable) */}
      {lesson.video_url && (
        <div className="mb-8 aspect-video overflow-hidden rounded-lg">
          <iframe
            src={lesson.video_url}
            className="h-full w-full"
            allowFullScreen
            title={lesson.title}
          />
        </div>
      )}

      <p className="mb-8 text-lg text-gray-80">{lesson.description}</p>

      {/* Markdown body (if any) */}
      {lesson.html && (
        <div
          className="content group mb-8 text-gray-80"
          dangerouslySetInnerHTML={{ __html: lesson.html }}
        />
      )}

      {/* Summary */}
      {lesson.summary_heading && (
        <div className="rounded-lg border border-[#DCDADA] p-6">
          <h2 className="mb-4 font-display text-xl font-bold text-charcoal">
            {lesson.summary_heading}
          </h2>
          {lesson.summary_list && (
            <ul className="space-y-2">
              {lesson.summary_list.map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-gray-80">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-dark" />
                  {item}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </>
  );

  if (!hasSidebar) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16 md:px-8">
        {mainContent}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-screen-xl px-4 py-16 md:px-8">
      <div className="flex flex-col md:flex-row md:gap-8 xl:gap-24">
        {/* Sidebar */}
        <div className="flex-none md:w-[17.18rem] md:pt-4">
          <div className="sticky md:top-28">
            {hasToc && <TableOfContents entries={lesson.toc} />}
            {hasToc && hasLearnMore && <div className="mt-6" />}
            {hasLearnMore && <LearnMoreLinks learn_more={lesson.learn_more!} />}
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1">
          {mainContent}
        </div>
      </div>
    </div>
  );
}
